/* eslint-disable no-debugger */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import firebase from 'firebase/app';

import GlobalLoginV2, { viewType } from '../../global/GlobalLoginV2';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import { useAlert } from '../../../providers/AlertProvider';

import {
  EMAIL_SEARCH_PARAM,
  REGISTER_FROM_PARAM,
  REGISTER_OPTIN_PARAM,
  REGISTER_SIGNUP_PARAM,
  TYPE_SEARCH_PARAM,
  AUTH_TOKEN_SEARCH_PARAM,
} from '../../config/routes';
import { updateUser } from '../../utils/service';
import { getDecryptedData } from '../../utils/crypto-utils';
import { finishProcess } from '../../utils/login';
import { getUUID } from '../../utils/utils';
import { getSearchParam } from '../../utils/routes';
import { getGenericError } from '../../utils/errors';
import { appendUtmParams } from '../../utils/url';
import safeStorage from '../../utils/localStorage';
import {
  getRedirectPath,
  getURIParam,
  getViewType,
  getWildfireDeviceId,
} from './utils';

import './Login.scss';

// TODO - Implement URI whitelist from env var
/* const uriWhitelist = [
  'chrome-extension://extensionidhere/auth.html',
  'https://closet.fordays.com', // Only for testing purposes
]; */

const getMfaSkipKey = (uid) => `mfa_enrollment_skipped_${uid}`;

const getMfaSkipCount = (uid) => {
  const count = parseInt(safeStorage.getItem(getMfaSkipKey(uid)), 10);
  return Number.isNaN(count) ? 0 : count;
};

const incrementMfaSkipCount = (uid) => {
  safeStorage.setItem(getMfaSkipKey(uid), String(getMfaSkipCount(uid) + 1));
};

const getNameSkipKey = (uid) => `name_form_skipped_${uid}`;

const getNameSkipCount = (uid) => {
  const count = parseInt(safeStorage.getItem(getNameSkipKey(uid)), 10);
  return Number.isNaN(count) ? 0 : count;
};

const incrementNameSkipCount = (uid) => {
  safeStorage.setItem(getNameSkipKey(uid), String(getNameSkipCount(uid) + 1));
};

const Login = () => {
  const { search } = useLocation();
  const {
    isSignedIn,
    isLoading: isLoadingFirebaseAuth,
    userData,
    reloadUserData,
    verifyEmailError,
    clearVerifyEmailError,
  } = useFirebaseAuth();
  const setAlert = useAlert();
  const posthog = usePostHog();
  const history = useHistory();

  const query = new URLSearchParams(search);
  const uriParam = getURIParam(query);
  const wfedidParam = getWildfireDeviceId(query);
  const typeParam = getSearchParam(query, TYPE_SEARCH_PARAM);
  const emailParam = getSearchParam(query, EMAIL_SEARCH_PARAM);
  const authToken = query.get(AUTH_TOKEN_SEARCH_PARAM) || '';

  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState(
    getViewType(typeParam) || viewType.VIEW_EMAIL_FORM,
  );
  const [finishProcessData, setFinishProcessData] = useState({});
  const phoneVerificationDoneRef = useRef(false);
  const nameFormDoneRef = useRef(false);

  const redirect = useCallback(async () => {
    if (!isSignedIn || !userData || isLoadingFirebaseAuth || isLoading) {
      return;
    }

    // Don't redirect during signup/verification/force-reset/phone-otp flow
    if (
      view === viewType.VIEW_VERIFY_EMAIL ||
      view === viewType.VIEW_EMAIL_PASSWORD_SIGNUP_FORM ||
      view === viewType.VIEW_FORCE_RESET_PASSWORD ||
      view === viewType.VIEW_PHONE_VERIFICATION ||
      view === viewType.VIEW_PHONE_OTP_LOGIN ||
      view === viewType.VIEW_NAME_FORM ||
      view === viewType.VIEW_SESSION_TIMEOUT
    ) {
      return;
    }

    // If the user's email is not verified, lock them on the verification screen.
    // This covers: page reload after signup, direct navigation while unverified, etc.
    // Google & email-link users always have emailVerified=true, so they pass through.
    const { currentUser } = firebase.auth();
    if (currentUser && !currentUser.emailVerified) {
      setView(viewType.VIEW_VERIFY_EMAIL);
      return;
    }

    // Auto-clear forcePwdReset when the user just signed in with a password
    // they already know (e.g. after using Firebase's "Reset password here" flow).
    // Firebase's auth_time claim tells us when the last sign-in happened;
    // if it was a password sign-in within the last 5 minutes, the forced reset
    // is redundant — clear the flag and let the user through.
    // Falls back to showing the force-reset form on any failure (no currentUser,
    // stale session, or backend error clearing the flag).
    if (userData?.forcePwdReset) {
      let skipForceReset = false;

      if (currentUser) {
        const RECENT_AUTH_THRESHOLD_MS = 5 * 60 * 1000;
        const tokenResult = await currentUser.getIdTokenResult();
        const authTimeMs = new Date(tokenResult.claims.auth_time * 1000).getTime();
        const authAgeSec = Math.round((Date.now() - authTimeMs) / 1000);
        const isRecentPasswordLogin = (
          tokenResult.signInProvider === 'password'
          && authAgeSec < RECENT_AUTH_THRESHOLD_MS / 1000
        );

        if (isRecentPasswordLogin) {
          const { error: clearError } = await updateUser({ forcePwdReset: false });

          if (!clearError) {
            await reloadUserData();
            skipForceReset = true;
          }

          console.log('[Login] forcePwdReset auto-clear:', {
            provider: tokenResult.signInProvider,
            authAgeSec,
            cleared: !clearError,
            ...(clearError && { error: clearError }),
          });
        } else {
          console.log('[Login] forcePwdReset requires manual reset:', {
            provider: tokenResult.signInProvider,
            authAgeSec,
          });
        }
      }

      if (!skipForceReset) {
        setView(viewType.VIEW_FORCE_RESET_PASSWORD);
        return;
      }
    }

    if (!userData.mfaEnabled &&
      getMfaSkipCount(userData?.uid) < 2 &&
      !phoneVerificationDoneRef.current) {
      setView(viewType.VIEW_PHONE_VERIFICATION);
      return;
    }

    if (!userData.firstName &&
      getNameSkipCount(userData?.uid) < 2 &&
      !nameFormDoneRef.current) {
      setView(viewType.VIEW_NAME_FORM);
      return;
    }

    setIsLoading(true);

    const { uid: userUid, error } = userData;

    if (error) {
      setAlert({
        type: 'notification',
        message: getGenericError(),
        error,
      });

      setIsLoading(false);
      return;
    }

    const data = await getDecryptedData(query, userUid);
    await finishProcess({
      ...finishProcessData,
      ...(REGISTER_SIGNUP_PARAM in data && {
        userRegistered: data[REGISTER_SIGNUP_PARAM],
      }),
      ...(REGISTER_OPTIN_PARAM in data && {
        optIn: data[REGISTER_OPTIN_PARAM],
      }),
      userData,
      search,
      wfExtensionFlow: uriParam && wfedidParam,
      wfedidParam,
      posthog,
    });

    window.location.href = appendUtmParams(getRedirectPath(
      query,
      getUUID(userData),
      history,
      view,
      data[REGISTER_FROM_PARAM],
    ));
  }, [
    isSignedIn,
    userData,
    search,
    isLoadingFirebaseAuth,
    isLoading,
    finishProcessData,
    view,
  ]);

  const handleForcePwdResetComplete = useCallback(async () => {
    setIsLoading(true);
    await reloadUserData();
    setView(viewType.VIEW_EMAIL_FORM);
    setIsLoading(false);
  }, [reloadUserData]);

  const handleOnViewChange = useCallback((currentView) => {
    setView(currentView);
  }, []);

  const handlePhoneVerificationDone = useCallback(() => {
    phoneVerificationDoneRef.current = true;
  }, []);

  const handlePhoneVerificationSkipped = useCallback(() => {
    phoneVerificationDoneRef.current = true;
    if (userData?.uid) {
      incrementMfaSkipCount(userData.uid);
    }
  }, [userData]);

  const handleNameFormDone = useCallback(() => {
    nameFormDoneRef.current = true;
  }, []);

  const handleNameFormSkipped = useCallback(() => {
    nameFormDoneRef.current = true;
    if (userData?.uid) {
      incrementNameSkipCount(userData.uid);
    }
  }, [userData]);

  const loginWithToken = useCallback(async () => {
    setIsLoading(true);
    await firebase.auth().signInWithCustomToken(authToken);
    setIsLoading(false);
  }, [authToken]);

  useEffect(() => {
    if (verifyEmailError) {
      setAlert({ type: 'notification', message: verifyEmailError });
      clearVerifyEmailError();
    }
  }, [verifyEmailError]);

  useEffect(() => {
    if (isSignedIn && userData && !isLoadingFirebaseAuth) {
      redirect();
    }
  }, [isSignedIn, userData, isLoadingFirebaseAuth, isLoading, view]);

  useEffect(() => {
    if (authToken) {
      loginWithToken();
    }
  }, []);

  return (
    <div className="Login">
      <GlobalLoginV2
        initialEmail={emailParam}
        onViewChange={handleOnViewChange}
        isParentLoading={isLoading || isLoadingFirebaseAuth}
        setFinishProcessData={setFinishProcessData}
        view={view}
        setView={setView}
        onForcePwdResetComplete={handleForcePwdResetComplete}
        onPhoneVerificationDone={handlePhoneVerificationDone}
        onPhoneVerificationSkipped={handlePhoneVerificationSkipped}
        onNameFormDone={handleNameFormDone}
        onNameFormSkipped={handleNameFormSkipped}
      />
    </div>
  );
};

export default Login;
